/**
 * OS's live call, held above every page (os-live-mode.md, Decided 14), so a
 * Layout switch keeps live mode going: the floating panel, the side sheet and
 * the full page each draw their own composer, and the call is no composer's.
 *
 * `CoachCallHost` sits above every page, the onboarding pages and the
 * `/v1/me` reload's splash included (`routes/__root.tsx`), and holds the one
 * `useCoachVoice`. A composer holds the call's thread while it is drawn
 * (`hold`), and starts a call on its own thread (`begin`); it reads `voice`
 * only when `threadId` is its own. The composer that holds the thread last is
 * the one the call reads its place from and tells of each kept turn.
 *
 * OS opened again over a call it was minimised or closed on tells the call
 * the person turned back to it (`attend`), as a click on the orb does.
 *
 * When the last box of the call's thread goes, the call ends, as it always
 * has — unless a Layout switch said it carries that thread (`carryCall`):
 * then it waits `CARRY_MS` for the box the new place draws (`call-hold.ts`).
 * One call at a time: Talk live on another thread ends the one that is up.
 *
 * The call writes into its thread's chat (step 3.10): each finished live line
 * as it is said, each kept turn in place of the lines it holds (by id, never
 * beside them), and the divider live mode leaves as it ends.
 *
 * Setup and Edit the setup hold the call too (step 5.4), on a setup seat
 * (`seat`): their thread is the setup's own conversation, the seat's `note`
 * frames reach the holder (`onNote`), and the holder adds what the setup's
 * start says beside the thread (`seatContext`: the language, the business it
 * sits beside, the region). A begin on the same thread on another seat (a
 * setup's Create carrying live mode on into the new business, `after:
 * 'create'`) moves the call quietly: the setup seat's call ends with no
 * chime, and the assistant's opens on the same thread.
 */
import type { AssistantLiveContext } from '@alluzo/shared';
import { useRouter } from '@tanstack/react-router';
import type { UIMessage } from 'ai';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { useCoachVoice, type CoachVoice, type LiveSeatNote, type StudioLiveFrame } from '~/features/voice';

import { useSessionStatus } from '@/hooks/use-session-status';
import { readDeviceLocale } from '@/lib/device-locale';

import { callDividerOf, finishedLinesOf, type CallLeftovers } from './call-lines';
import { CARRY_MS, callReleaseOf, callTenantOf, conversationShownOf, seatContextOf, type CallSeat } from './call-hold';
import type { CoachThread } from './coach-chats';
import { useCoachStore } from './coach-store';
import { liveDividerMessageOf, liveNowInMessageOf, upsertMessages } from './live-thread';
import type { CoachSurface } from './surface';
import { livePlaceContext } from './use-live-place';

/** What the call reads of a thread: a conversation's, or a setup's own (which has no business, and no record). */
export type CallThread = Pick<CoachThread, 'id' | 'tenantId' | 'chat' | 'about'>;

/** A composer drawing a thread: the call reads it while it is the thread's newest. */
export type CallHolder = {
  thread: CallThread;
  surface: CoachSurface;
  /** A spoken turn was kept into the thread. */
  onKept: () => void;
  /** The setup seat the call opens on (step 5.1); absent is the assistant. On `setup` the call has no business. */
  seat?: CallSeat;
  /** `create`: live mode carries on after a setup's Create rather than starting (step 5.2). */
  after?: 'create';
  /** With `after: 'create'`: the business live mode carries on into, for the thread's "Now in <name>." */
  nowIn?: string;
  /** A setup seat's note on one of the person's lines (step 5.4). */
  onNote?: (note: LiveSeatNote) => void;
  /** What a setup seat's start says beside the thread: the language, the business it sits beside, the device's region. */
  seatContext?: () => Readonly<Record<string, unknown>>;
  /** The setup studio's session (a uuid) a setup seat drafts into (7.5); absent is the legacy setup seat. */
  setupSession?: string;
  /** The studio tab the call opens from (9.4), its `X-Setup-Client`. */
  setupClient?: string;
  /** The studio seat's frames (7.5). */
  onStudioFrame?: (frame: StudioLiveFrame) => void;
};

export type CoachCall = {
  readonly voice: CoachVoice;
  /** The thread the call is on (up, owed, or the last one), or null before the first. */
  readonly threadId: string | null;
  /** What the voice still held of the call before the last begin: none of it is this call's (`call-lines.ts`). */
  readonly left: CallLeftovers | null;
  /** Talk live on this composer's thread. */
  readonly begin: (holder: CallHolder) => void;
  /** This composer draws its thread now; the returned function lets go of it. */
  readonly hold: (holder: CallHolder) => () => void;
};

const CoachCallContext = createContext<CoachCall | null>(null);

export const useCoachCall = (): CoachCall => {
  const call = useContext(CoachCallContext);
  if (call === null) throw new Error('useCoachCall needs CoachCallHost above it');
  return call;
};

/** The thread a Layout switch is moving now: its call waits for the next box. */
let carrying: string | null = null;

/** The thread a call is up on, or still owes an answer to; null with none. */
let onCall: string | null = null;

/** A Layout switch moves this thread to another place: its call, if one is up, goes with it. */
export const carryCall = (threadId: string | null): void => {
  carrying = threadId;
};

/** Whether a call is up on this thread now, or still owes it an answer. */
export const callIsOn = (threadId: string): boolean => onCall === threadId;

/** A typed turn is on its way in this thread: its reply streams at the end. */
const typedInFlight = (thread: CallThread): boolean => thread.chat.status === 'submitted' || thread.chat.status === 'streaming';

/**
 * Messages the server already kept, put into the thread's chat by id: a
 * message under an id the thread holds (a live line drawn as it was said)
 * takes its place, never a second row. `lineOnly`: finished live lines, which
 * change only the words of a message already there. Answers whether the
 * thread changed.
 */
const keepInto = (thread: CallThread, incoming: readonly UIMessage[], lineOnly = false): boolean => {
  const held = thread.chat.messages;
  const next = upsertMessages(held, incoming, { typedInFlight: typedInFlight(thread), lineOnly });
  if (next === held) return false;
  thread.chat.messages = [...next];
  return true;
};

export function CoachCallHost({ children }: { readonly children: ReactNode }): ReactElement {
  const router = useRouter();
  const bump = useCoachStore((store) => store.bump);
  /** The composer the call reads: the one that began it, then the newest to hold its thread. */
  const owner = useRef<CallHolder | null>(null);
  const holders = useRef(new Set<CallHolder>());
  // No business on the setup seat (step 5.2): the host sits above the onboarding pages too.
  const [call, setCall] = useState<{
    readonly threadId: string;
    readonly tenantId: string | null;
    readonly seat: CallSeat | undefined;
    readonly after: 'create' | undefined;
    readonly nonce: number;
    /** What the voice still held of the call before this begin: never this call's to write. */
    readonly left: CallLeftovers;
  } | null>(null);

  // Minimised counts for the call only when the minimised panel draws the call's thread.
  const dockMinimised = useCoachStore((store) => store.dockOpen && store.minimised && call !== null && store.dockThreadId === call.threadId);
  const voice = useCoachVoice({
    tenantId: call?.tenantId ?? null,
    ...(call?.seat === undefined ? {} : { seat: call.seat }),
    onNote: (note) => owner.current?.onNote?.(note),
    onStudioFrame: (frame) => owner.current?.onStudioFrame?.(frame),
    context: (): AssistantLiveContext => {
      // `begin` sets the owner before any call starts.
      const held = owner.current as CallHolder;
      const thread = held.thread;
      const tz = readDeviceLocale().timeZone;
      return {
        threadId: thread.id,
        messages: thread.chat.messages,
        // Where the person is now, from the router, and what that page shows
        // (os-live-mode.md 2.3); on the OS page, the thread's own place. A
        // setup seat has no business place: its own screen.
        ...(held.seat === undefined ? livePlaceContext(thread.tenantId, router.state.location.pathname, thread.about) : { screen: 'setup' }),
        ...(tz === null ? {} : { tz }),
        // Talk live from the chat never greets (S9): the thread is already open.
        greet: false,
        ...held.seatContext?.(),
        ...seatContextOf(held),
      } as AssistantLiveContext;
    },
    onTurn: (messages) => {
      const held = owner.current;
      if (held === null || !keepInto(held.thread, messages)) return;
      // The server keeps the thread now, or keeps it newer: the lists read again.
      bump();
      held.onKept();
    },
    // Minimised OS, or a hidden tab, hides its cards: a spoken yes opens OS, so the card can be looked at.
    conversationShown: () =>
      conversationShownOf({
        minimised: owner.current?.surface === 'dock' && useCoachStore.getState().minimised,
        tabHidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
      }),
    onCardHidden: () => useCoachStore.getState().expand(),
    // Minimised, the person is not looking at OS, whatever the tab.
    minimised: dockMinimised,
  });

  // Every finished live line joins the thread under its id as it is said
  // (C10): the greeting, the person's settled line, a fixed line, small
  // talk. The server keeps the same lines under the same ids (2.10), so the
  // kept turn that follows takes their place rather than adding beside them.
  // A begin on another thread names it before the new call's start resets the
  // voice: what the call before left (`left`) is never written into it.
  const { transcript } = voice.live;
  const left = call?.left ?? null;
  useEffect(() => {
    const held = owner.current;
    if (held === null || held.thread.id !== call?.threadId) return;
    const finished = finishedLinesOf(transcript, left);
    if (finished.length > 0) keepInto(held.thread, finished, true);
  }, [transcript, call?.threadId, left]);

  // The divider live mode leaves where it ended, under the id the server keeps it by.
  const divider = callDividerOf(voice.divider, left);
  useEffect(() => {
    const held = owner.current;
    if (divider === null || held === null || held.thread.id !== call?.threadId) return;
    if (keepInto(held.thread, [liveDividerMessageOf(divider.id, divider.data) as UIMessage])) bump();
  }, [divider, call?.threadId, bump]);

  // Live mode carried on after a setup's Create: "Now in <name>." leads it, under the id the server keeps it by.
  // The call before this one (the setup seat's) keeps its id until the new one opens: that id is never this one's.
  const { callId } = voice.live;
  const nowInFor = useRef<string | null>(null);
  const before = useRef<string | null>(null);
  const callIdNow = useRef(callId);
  const nonce = call?.nonce ?? 0;
  useEffect(() => {
    before.current = callIdNow.current;
  }, [nonce]);
  useEffect(() => {
    callIdNow.current = callId;
    const held = owner.current;
    const name = held?.nowIn?.trim() ?? '';
    if (call?.after !== 'create' || callId === null || callId === before.current || nowInFor.current === callId) return;
    if (held === null || name === '' || held.thread.id !== call.threadId) return;
    nowInFor.current = callId;
    if (keepInto(held.thread, [liveNowInMessageOf(callId, name) as UIMessage])) bump();
  }, [call?.after, call?.threadId, callId, bump, nonce]);

  // Read by the release below, which runs in a cleanup after this render.
  const now = useRef<{ voice: CoachVoice; threadId: string | null; seat: CallSeat | undefined; after: 'create' | undefined }>({
    voice,
    threadId: call?.threadId ?? null,
    seat: undefined,
    after: undefined,
  });
  useEffect(() => {
    now.current = { voice, threadId: call?.threadId ?? null, seat: call?.seat, after: call?.after };
    onCall = voice.live.active || voice.live.owed ? (call?.threadId ?? null) : null;
  });
  useEffect(
    () => () => {
      onCall = null;
    },
    [],
  );

  // A begin starts once its thread's tenant is what the call reads.
  const { begin: startVoice } = voice;
  useEffect(() => {
    if (call !== null) startVoice();
    // Only a new begin starts a call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.nonce]);

  // OS opened again over its call (the minimised pill, Ask, a door): the person turned back to it, and the call is
  // told as a click on the orb tells it (`attend`), so the line they say next is OS's and its window opens (12 Oct r3, item C).
  const dockShown = useCoachStore((store) => store.dockOpen && !store.minimised && call !== null && store.dockThreadId === call.threadId);
  const shownBefore = useRef(dockShown);
  const { attend, active: callUp } = voice.live;
  useEffect(() => {
    const reopened = dockShown && !shownBefore.current;
    shownBefore.current = dockShown;
    if (reopened && callUp) attend();
  }, [dockShown, callUp, attend]);

  // Said to the store: Ask at the foot of the page, the panel's head and the pill carry the call's timer.
  const calling = voice.live.active;
  const setCallUp = useCoachStore((store) => store.setCall);
  useEffect(() => {
    setCallUp(calling);
  }, [calling, setCallUp]);
  useEffect(() => () => setCallUp(false), [setCallUp]);
  const owed = voice.live.owed;
  const setCallOwed = useCoachStore((store) => store.setCallOwed);
  useEffect(() => {
    setCallOwed(owed);
  }, [owed, setCallOwed]);

  const waitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopWaiting = (): void => {
    if (waitTimer.current !== null) clearTimeout(waitTimer.current);
    waitTimer.current = null;
  };

  const begin = useCallback((holder: CallHolder) => {
    const { voice: held, threadId, seat, after } = now.current;
    const up = held.live.active || held.live.owed;
    // The same thread on another seat moves the call (Create carries it on): the old one ends with no chime.
    const moves = up && threadId === holder.thread.id && (seat !== holder.seat || after !== holder.after);
    if (moves) held.live.stop({ quiet: true });
    else if (up && threadId !== holder.thread.id) held.live.stop();
    owner.current = holder;
    now.current = { ...now.current, seat: holder.seat, after: holder.after };
    const left: CallLeftovers = { transcript: held.live.transcript, divider: held.divider };
    setCall((last) => ({ threadId: holder.thread.id, tenantId: callTenantOf(holder), seat: holder.seat, after: holder.after, nonce: (last?.nonce ?? 0) + 1, left }));
  }, []);

  const hold = useCallback((holder: CallHolder) => {
    holders.current.add(holder);
    const id = holder.thread.id;
    if (now.current.threadId === id) {
      owner.current = holder;
      stopWaiting();
      // The move is over once the new box is up: taken on the next tick, so
      // the mount React runs twice in development still finds it carried.
      setTimeout(() => {
        if (carrying === id && holders.current.has(holder)) carrying = null;
      }, 0);
    }
    return () => {
      holders.current.delete(holder);
      const left = [...holders.current].filter((one) => one.thread.id === id);
      if (owner.current === holder && left.length > 0) owner.current = left[left.length - 1] ?? null;
      const { voice: held, threadId } = now.current;
      const up = held.live.active || held.live.owed;
      // Stopped, with an answer owed: it still lands in the thread (Stop live, or Close in the panel, C2/C3).
      const onlyOwed = !held.live.active && held.live.owed;
      const release = callReleaseOf({ callThread: up ? threadId : null, released: id, holdersLeft: left.length, carrying, onlyOwed });
      if (release === 'end') held.live.stop();
      if (release !== 'wait') return;
      stopWaiting();
      waitTimer.current = setTimeout(() => {
        waitTimer.current = null;
        if (carrying === id) carrying = null;
        if ([...holders.current].some((one) => one.thread.id === id)) return;
        if (now.current.threadId === id) now.current.voice.live.stop();
      }, CARRY_MS);
    };
  }, []);

  useEffect(() => () => stopWaiting(), []);

  // Above every gate, the host outlives a sign-out: live mode does not (a sign-out unmounted it when the host sat in the shell).
  const session = useSessionStatus();
  useEffect(() => {
    if (session !== 'signed-out') return;
    const held = now.current.voice;
    if (held.live.active || held.live.owed) held.live.stop();
  }, [session]);

  const value = useMemo<CoachCall>(() => ({ voice, threadId: call?.threadId ?? null, left, begin, hold }), [voice, call?.threadId, left, begin, hold]);
  return <CoachCallContext.Provider value={value}>{children}</CoachCallContext.Provider>;
}
